import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import AdSenseConfig from '../../config/adsense.config';
import { hasPremium } from '../../constants/function';
import ApiService from '../../services/api-service';
import { loadAdSense } from '../../utils/loadAdSense';

const WIDTH_RETRY_LIMIT = 10;
const WIDTH_RETRY_MS = 200;

/**
 * AdSense display unit.
 *
 * - Renders nothing for Premium users (ad-free is part of the plan).
 * - Loads the AdSense script on demand, so no ad request fires on screens
 *   that have no unit (landing, login, policies, OBS).
 * - Waits until the slot has a measurable width before pushing. Pushing at
 *   width 0 makes Google request a 0x0 ad that is always "unfilled".
 * - Stays collapsed until Google marks the slot filled, so an unfilled slot
 *   never leaves an empty box on the page.
 * - Reports each filled slot once to skcore, which books it at the
 *   configured estimate; skpay pays the listener's recent plays from it.
 */
const reportImpression = (adSlot) => {
  ApiService.AD_IMPRESSION({
    impressionId: uuidv4(),
    platform: 'web',
    format: 'display',
    adUnitId: adSlot,
    shownAt: new Date().toISOString(),
  }).catch((error) => console.warn('[AdSenseAd] impression not recorded', error));
};
export function AdSenseAd({
  adSlot,
  format = 'auto',
  responsive = true,
  style = {}
}) {
  const adRef = useRef(null);
  const isPushed = useRef(false);
  const [filled, setFilled] = useState(false);

  const enabled = Boolean(adSlot) && AdSenseConfig.isConfigured() && !hasPremium();

  useEffect(() => {
    if (!enabled) return undefined;

    const el = adRef.current;
    if (!el) return undefined;

    let cancelled = false;
    let timer = null;
    let attempts = 0;

    const observer = new MutationObserver(() => {
      if (el.getAttribute('data-ad-status') === 'filled') {
        setFilled(true);
        observer.disconnect();
        reportImpression(adSlot);
      }
    });
    observer.observe(el, { attributes: true, attributeFilter: ['data-ad-status'] });

    const tryPush = () => {
      if (cancelled || isPushed.current) return;
      if (el.offsetWidth === 0) {
        if (attempts < WIDTH_RETRY_LIMIT) {
          attempts += 1;
          timer = setTimeout(tryPush, WIDTH_RETRY_MS);
        }
        return;
      }
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isPushed.current = true;
      } catch (error) {
        console.warn('[AdSenseAd] push failed', error);
      }
    };

    loadAdSense().then((loaded) => {
      if (loaded && !cancelled) tryPush();
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [adSlot, enabled]);

  if (!enabled) return null;

  return (
    <div
      data-testid="adsense-slot"
      style={{
        width: '100%',
        overflow: 'hidden',
        height: filled ? 'auto' : 0,
        opacity: filled ? 1 : 0,
        transition: 'opacity 0.3s',
      }}
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{
          display: 'block',
          ...style
        }}
        data-ad-client={AdSenseConfig.publisherId}
        data-ad-slot={adSlot}
        data-ad-format={format}
        data-full-width-responsive={responsive}
      />
    </div>
  );
}

export default AdSenseAd;
